import React from "react";
import { Box, Stack, Typography } from "@mui/material";

export type HelpGuideMeta = {
  slug: string;
  title: string;
  description: string;
  category: "Microsoft 365" | "Documentation platforms" | "Search & AI" | "PSA";
  featured: boolean;
};

const accent = "#705d4a";

const P = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 15, lineHeight: 1.7, color: "#334155", mb: 2 }}>
    {children}
  </Typography>
);

const H = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="h2"
    sx={{
      fontFamily: "var(--theo-font-sans)",
      fontSize: 18,
      fontWeight: 700,
      color: "#0f172a",
      mt: 3,
      mb: 1.25,
    }}
  >
    {children}
  </Typography>
);

const Li = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="li"
    sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 15, lineHeight: 1.65, color: "#334155", mb: 0.75 }}
  >
    {children}
  </Typography>
);

const PromptList = ({ prompts }: { prompts: string[] }) => (
  <Stack spacing={1.1} sx={{ mb: 2 }}>
    {prompts.map((prompt) => (
      <Box
        key={prompt}
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          background: "#fff",
          px: 1.5,
          py: 1.25,
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: 14,
            lineHeight: 1.6,
            color: "#0f172a",
          }}
        >
          {prompt}
        </Typography>
      </Box>
    ))}
  </Stack>
);

export const HELP_CENTER_GUIDE_METAS: HelpGuideMeta[] = [
  {
    slug: "m365-actions",
    title: "M365 Actions",
    description:
      "How Theo uses Microsoft 365 tools in chat, approvals, and customer context.",
    category: "Microsoft 365",
    featured: true,
  },
  {
    slug: "it-glue",
    title: "Working with IT Glue",
    description: "Connect IT Glue and ask Theo to read or update documentation records.",
    category: "Documentation platforms",
    featured: true,
  },
  {
    slug: "hudu",
    title: "Working with Hudu",
    description: "Use Hudu-backed playbooks for articles, assets, companies, and passwords.",
    category: "Documentation platforms",
    featured: false,
  },
  {
    slug: "ai-search-tickets-documents",
    title: "AI Search for Tickets and Documents",
    description: "Semantic search over tickets and knowledge bases through Theo.",
    category: "Search & AI",
    featured: true,
  },
  {
    slug: "theo-in-autotask",
    title: "Theo in Autotask",
    description: "Ticket chat, queues, and PSA actions when your MSP uses Autotask.",
    category: "PSA",
    featured: false,
  },
  {
    slug: "theo-in-halopsa",
    title: "Theo in HaloPSA",
    description: "Queues, ticket assistant, and Halo workflows with Theo.",
    category: "PSA",
    featured: false,
  },
];

export function getHelpCenterGuideTitleFromPath(pathname: string): string | null {
  if (!pathname.startsWith("/help-center/guides/")) return null;
  const slug = pathname.replace("/help-center/guides/", "").split("/")[0];
  if (!slug) return null;
  const meta = HELP_CENTER_GUIDE_METAS.find((g) => g.slug === slug);
  return meta?.title ?? null;
}

export const HELP_CENTER_GUIDE_BODIES: Record<string, React.ReactNode> = {
  "m365-actions": (
    <Stack spacing={0}>
      <P>
        Theo&apos;s Microsoft 365 guide is broad by design: it covers email and mailbox troubleshooting, user and
        group lifecycle, licensing, Teams collaboration, SharePoint and OneDrive access, archive and retention work,
        reporting, and security checks. In practice, M365 is one of Theo&apos;s richest toolsets, so it works best
        when you ask for one concrete outcome at a time.
      </P>
      <H>What Theo can do well</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          Mail and mailbox work: inbox rules, forwarding, signatures, quota checks, junk folder checks, message
          trace, quarantine review, and end-to-end email diagnostics.
        </Li>
        <Li>
          Identity and administration: user profile lookups, password resets, block/unblock, session revocation,
          group membership changes, license assignment, and manager updates.
        </Li>
        <Li>
          Collaboration and content: Teams chats/channels, online meetings, room mailbox workflows, file browsing,
          SharePoint permissions, and site / library exploration.
        </Li>
        <Li>Security and operations: suspicious login review, compromised-account checks, security audit, and audit logs.</Li>
      </Box>
      <H>Tips and tricks</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          Use <strong>email addresses / UPNs</strong> instead of display names whenever possible. That lines up with how
          many M365 actions are documented in the prompt library and avoids ambiguous lookups.
        </Li>
        <Li>
          For mailbox investigations, ask Theo to <strong>diagnose</strong> rather than only checking one signal. The
          email tools span message trace, rules, quarantine, junk, quota, and forwarding, so broader prompts unlock
          better workflows.
        </Li>
        <Li>
          For SharePoint and OneDrive, be specific about the <strong>site, folder, file path, or user</strong>. The
          tools include exploration helpers, permission helpers, and path-based lookups, which work best with context.
        </Li>
        <Li>
          For Teams and group changes, start with a read prompt first if you are unsure of the right target, then follow
          with the write action once Theo resolves the object.
        </Li>
      </Box>
      <H>Sample prompts</H>
      <PromptList
        prompts={[
          "Reset password for jane@contoso.com and revoke active sessions.",
          "Check why alex@contoso.com is not getting emails. Look at message trace, rules, quarantine, and quota.",
          "Show me all groups for chris@contoso.com and tell me whether they own any of them.",
          "Grant maria@contoso.com read access to the Finance calendar.",
          "List permissions for this SharePoint folder and then give sam@vendor.com read access.",
          "Run a Microsoft 365 security audit and highlight anything risky.",
        ]}
      />
    </Stack>
  ),
  "it-glue": (
    <Stack spacing={0}>
      <P>
        Theo&apos;s IT Glue functionality spans the parts of the product technicians touch every day: organizations,
        contacts, documents, document sections, password records, password folders, flexible assets, groups, and
        related metadata like organization types or password categories.
      </P>
      <H>What Theo can do well</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>Browse and retrieve structured documentation: documents, sections, password records, contacts, and assets.</Li>
        <Li>Create and update records when you already know the organization, type, folder, or object you want.</Li>
        <Li>
          Use search-style flows to resolve IDs before changing data, especially for flexible assets, folders, and
          organizations.
        </Li>
      </Box>
      <H>Tips and tricks</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          For flexible assets, ask Theo to <strong>resolve the asset type first</strong>. The capabilities doc makes it
          clear that type discovery is a separate step, and many flexible-asset operations depend on that type id.
        </Li>
        <Li>
          For documents and passwords, include the <strong>organization</strong> in your request. IT Glue has lots of
          similarly named records across customers.
        </Li>
        <Li>
          If you want Theo to write documentation, tell it whether you want a <strong>new document</strong>, a new
          <strong> section</strong>, or an <strong>update</strong> to an existing record.
        </Li>
        <Li>
          Theo can also use KB-style search for IT Glue documents, so natural-language prompts work well when you know
          the content you need but not the exact document name.
        </Li>
      </Box>
      <H>Sample prompts</H>
      <PromptList
        prompts={[
          "Search IT Glue documents for the VPN setup for Acme.",
          "Find the password folder for Acme firewall credentials and list the records in it.",
          "Create a contact for Sarah Lee under Acme and add her work email and title.",
          "Search flexible asset types for server-related assets, then create a flexible asset for this host.",
          "Update the document section for Acme onboarding notes with the latest admin checklist.",
          "Get the password record for the M365 break-glass account for Acme.",
        ]}
      />
    </Stack>
  ),
  hudu: (
    <Stack spacing={0}>
      <P>
        Theo&apos;s Hudu coverage is strong across articles, article folders, companies, assets, asset layouts,
        passwords, and password folders. That makes Hudu a good fit for both retrieval workflows and light
        documentation maintenance inside chat.
      </P>
      <H>What Theo can do well</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          Retrieve existing documentation quickly: article lookups, article-folder browsing, company details, asset
          records, password records, and folder contents.
        </Li>
        <Li>
          Create or update Hudu content, including company records, assets, articles, passwords, and folder structure.
        </Li>
        <Li>Archive / unarchive workflows where you want to keep data but remove it from the active view.</Li>
      </Box>
      <H>Tips and tricks</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          Ask with a <strong>company name</strong> whenever possible. Many Hudu actions are company-scoped, especially
          assets and passwords.
        </Li>
        <Li>
          If you are not sure where something lives, start with a <strong>search</strong> prompt instead of a create
          prompt. Hudu has separate list, search, and get actions for most record types.
        </Li>
        <Li>
          For cleanup workflows, prefer <strong>archive</strong> before delete when the goal is to hide rather than
          permanently remove.
        </Li>
        <Li>
          Asset layouts are useful discovery tools: ask Theo to look up the layout before creating or updating an asset
          so the right fields are used.
        </Li>
      </Box>
      <H>Sample prompts</H>
      <PromptList
        prompts={[
          "Search Hudu articles for backup restore instructions for Acme.",
          "List password folders for Acme and then show me the firewall passwords.",
          "Get the Hudu asset for server SRV-APP-01 under Acme.",
          "Create a new Hudu article folder for Acme network documentation.",
          "Archive the old Hudu password record for the retired VPN account.",
          "List asset layouts and help me choose the right one before creating a new asset.",
        ]}
      />
    </Stack>
  ),
  "ai-search-tickets-documents": (
    <Stack spacing={0}>
      <P>
        Theo can search beyond exact keywords. The ticket and document search experience is built around semantic
        retrieval, so technicians can ask for similar tickets, likely resolutions, or matching knowledge content even
        when the exact words differ from the source material.
      </P>
      <H>What Theo can do well</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          Search <strong>knowledge documents</strong> when you need procedures, notes, or runbooks.
        </Li>
        <Li>
          Search <strong>historical tickets</strong> when you need similar incidents or prior resolutions.
        </Li>
        <Li>
          Combine search with in-ticket AI features like the <strong>AI Overview</strong> to speed up triage and next
          steps.
        </Li>
      </Box>
      <H>Tips and tricks</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          Phrase prompts around the <strong>issue and the outcome</strong>, not just a noun. Search works best when the
          prompt sounds like a technician describing what is happening.
        </Li>
        <Li>
          If the first result set is too broad, add a <strong>customer name</strong>, product name, time window, or a
          detail about the failure mode to narrow the search.
        </Li>
        <Li>
          If you need a likely answer for the current incident, search first and then use <strong>AI Overview</strong>
          or the ticket chat to turn those findings into the next action.
        </Li>
        <Li>
          KB-style search is especially helpful when you remember the intent of a document but not its exact title.
        </Li>
      </Box>
      <H>Sample prompts</H>
      <PromptList
        prompts={[
          "Find similar tickets where Outlook kept prompting for MFA after a password reset.",
          "Search documentation for VPN setup steps for a new laptop.",
          "Show tickets with similar resolution patterns for failed Windows patch installs.",
          "Search KB content for how we onboard a new M365 shared mailbox.",
          "Find past tickets for this client involving printer offline issues and summarize the likely fixes.",
          "Search ticket history for incidents like this one, focusing on resolution summaries.",
        ]}
      />
    </Stack>
  ),
  "theo-in-autotask": (
    <Stack spacing={0}>
      <P>
        Theo in Autotask is about helping technicians work tickets faster without bouncing between systems. The available
        Autotask actions cover contacts and devices, ticket communication, status changes, assignments, checklist work,
        additional contacts, secondary resources, time entries, appointments, service calls, and ticket detail/search.
      </P>
      <H>What Theo can do well</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>Handle in-ticket work: reply, summarize and reply, add notes, update status, and close with time entry.</Li>
        <Li>Maintain ticket structure: checklist items, additional contacts, secondary resources, and field updates.</Li>
        <Li>Support scheduling workflows: appointments, company to-dos, service calls, and service-call ticket links.</Li>
        <Li>Look up surrounding context: contacts, customers, devices, raw ticket details, resource ids, and resource roles.</Li>
      </Box>
      <H>Tips and tricks</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          Use the <strong>ticket thread</strong> for ticket-specific work. Theo is strongest there because the context
          already includes the ticket id.
        </Li>
        <Li>
          When assigning tickets, ask Theo to resolve the <strong>resource id</strong> or list <strong>resource
          roles</strong> first if you do not know the exact Autotask identifiers.
        </Li>
        <Li>
          Be explicit about whether you want a <strong>public reply</strong>, an <strong>internal note</strong>, or a
          <strong> status-only</strong> change. Autotask has distinct actions for those.
        </Li>
        <Li>
          For checklists, additional contacts, and secondary resources, Theo can both read and write. Ask for a read
          step first if you want to review the current state before changing it.
        </Li>
      </Box>
      <H>Sample prompts</H>
      <PromptList
        prompts={[
          "Reply to ticket 12345 with an update message for the customer.",
          "Summarize the work done and reply to ticket 12345.",
          "Update the status of ticket 12345 to In Progress only.",
          "Assign ticket 12345 to Yogesh and resolve the right resource id and role.",
          "Show checklist items on ticket 12345 and mark the final step complete.",
          "Log 30 minutes on ticket 12345, then close it with the time entry if everything looks done.",
        ]}
      />
    </Stack>
  ),
  "theo-in-halopsa": (
    <Stack spacing={0}>
      <P>
        Theo in HaloPSA focuses on ticket execution and surrounding customer context. The Halo action set covers
        customers, contacts, devices, ticket replies, summarization, notes, status changes, assignment, time entries,
        close-with-time-entry, and ticket search.
      </P>
      <H>What Theo can do well</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>Find the right company, contact, or device context before you act on a ticket.</Li>
        <Li>Draft or send ticket replies, add notes, update ticket status, assign work, and log time.</Li>
        <Li>Search Halo tickets and related entities when you need prior context before responding.</Li>
      </Box>
      <H>Tips and tricks</H>
      <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
        <Li>
          Ask Theo to <strong>search contacts or devices first</strong> when the requester mentions a person or machine
          informally. That produces cleaner ticket actions later.
        </Li>
        <Li>
          Separate <strong>status updates</strong>, <strong>assignment</strong>, and <strong>reply text</strong> in your
          wording if you want tight control over what Theo changes.
        </Li>
        <Li>
          Use <strong>summarize and reply</strong> when you want technician work translated into customer-facing
          language; use <strong>add note</strong> when the content should stay internal.
        </Li>
        <Li>
          If the ticket is essentially complete, ask for <strong>close with time entry</strong> instead of doing those as
          separate steps.
        </Li>
      </Box>
      <H>Sample prompts</H>
      <PromptList
        prompts={[
          "Search Halo contacts for John at Acme and show me the likely requester.",
          "Reply to this Halo ticket with a concise customer update and next steps.",
          "Add an internal note to the Halo ticket summarizing the troubleshooting already completed.",
          "Assign this ticket to the network engineer and set the status to In Progress.",
          "Log time on this Halo ticket and then close it if the work is complete.",
          "Search Halo devices for the user’s laptop before updating the ticket.",
        ]}
      />
    </Stack>
  ),
};
